/**
 * FmLayout — core layout/rail primitives: FmSplit, FmContentWidth, FmShellSection.
 *
 * FmSplit: the only two-column split — three recipe tokens, side start/end,
 *   stack order, sticky graphic and a no-copy collapse.
 * FmContentWidth: max-width/measure wrapper using --fm-content-max DS token.
 * FmShellSection: page-body anatomy (header band + body + optional aside).
 *
 * Report composition (nav / section bands / report section) lives in
 * FmReportComposition; form controls (control surface / field) in FmField;
 * shared helpers/types in fmLayoutShared.
 *
 * DUAL-THEME solid surfaces paint DS tokens; NO hardcoded hex, Tailwind, or AntD.
 */
import React from 'react';
import { shouldStackForTier } from './FmResponsive';
import type { FmDeviceTier } from './FmResponsive';
import { cx, widthTokenFor, type FmContentWidthSize } from './fmLayoutShared';
import './FmLayout.css';

// FmSplit

/** Which side the active (graphic) element occupies on desktop. */
export type FmSplitSide = 'start' | 'end';
/** The three split recipes (tokens.css): equal, graphic twice the copy, copy as a narrow panel. */
export type FmSplitBalance = 'balanced' | 'media-dominant' | 'copy-narrow';
export type FmSplitAlign = 'start' | 'center' | 'stretch';
export type FmSplitContentSurface = 'solid' | 'plain';
export type FmSplitStackOrder = 'content-first' | 'graphic-first';

export interface FmSplitProps {
  /** Copy slot. When absent the split collapses to the graphic at full width. */
  content?: React.ReactNode;
  /** Graphic / map / chart slot. May be glass when graphicGlass=true. */
  graphic: React.ReactNode;
  /** Enable glass treatment (backdrop-filter, --fm-glass tokens) on graphic slot. */
  graphicGlass?: boolean;
  /** Force the stacked single-column layout at every width. */
  stack?: boolean;
  /**
   * Device tier (FmResponsive contract). 'tablet' and 'mobile' force the
   * stacked layout; wins over an explicit `stack={false}`.
   */
  tier?: FmDeviceTier;
  /** Desktop side of the graphic. `end` keeps the recipe's track order; `start` mirrors it. */
  side?: FmSplitSide;
  /** Column recipe. */
  balance?: FmSplitBalance;
  /** Vertical alignment for the two slots. */
  align?: FmSplitAlign;
  /** Stacked reading order. Desktop order follows `side`. */
  stackOrder?: FmSplitStackOrder;
  /** Keep the graphic in view while the copy scrolls. Applies only to the two-column layout. */
  sticky?: boolean;
  /** Content is solid by default; story sections use plain copy rails on their band. */
  contentSurface?: FmSplitContentSurface;
  /**
   * @internal DS-internal class hook; forbidden for route-owned layout.
   */
  className?: string;
  'data-testid'?: string;
}

/**
 * The only two-column split. Geometry comes from the three recipe tokens; tone
 * comes from the enclosing FmSectionBand (surfaces read band-resolved
 * `--fm-d-*` tokens), so the split never sets its own theme.
 */
export const FmSplit: React.FC<FmSplitProps> = ({
  content,
  graphic,
  graphicGlass = false,
  stack = false,
  tier,
  side = 'end',
  balance = 'balanced',
  align = 'start',
  stackOrder = 'content-first',
  sticky = false,
  contentSurface = 'solid',
  className,
  'data-testid': dataTestId,
}) => {
  const graphicOnly = content == null || content === false;
  const stacked = stack || (tier !== undefined && shouldStackForTier(tier));
  const modifiers: Record<string, boolean> = {
    'fm-split--stack': stacked,
    'fm-split--graphic-only': graphicOnly,
    'fm-split--side-start': side === 'start',
    [`fm-split--${balance}`]: balance !== 'balanced',
    [`fm-split--align-${align}`]: align !== 'start',
    'fm-split--stack-graphic-first': stackOrder === 'graphic-first',
    'fm-split--sticky': sticky,
    'fm-split--graphic-glass': graphicGlass,
    'fm-split--content-solid': contentSurface === 'solid',
  };
  return (
    <div
      className={cx(
        'fm-split',
        ...Object.keys(modifiers).filter((name) => modifiers[name]),
        className,
      )}
      data-testid={dataTestId}
    >
      {graphicOnly ? null : (
        <div
          className="fm-split__content"
          data-testid={dataTestId ? `${dataTestId}-content` : undefined}
        >
          {content}
        </div>
      )}
      <div
        className="fm-split__graphic"
        data-testid={dataTestId ? `${dataTestId}-graphic` : undefined}
      >
        {graphic}
      </div>
    </div>
  );
};

// FmContentWidth

export interface FmContentWidthProps {
  children: React.ReactNode;
  width?: FmContentWidthSize;
  /** Optional vertical page padding owned by the width primitive. */
  padY?: 'none' | 'sm' | 'md' | 'lg';
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  /**
   * @internal DS/migration class hook; forbidden for route-owned layout, colour,
   * spacing, and width wrapping chrome. Add a named variant instead.
   */
  'data-testid'?: string;
}

/**
 * Max-width/measure wrapper enforcing the DS --fm-content-max token.
 * Keeps dense pages from sprawling across ultra-wide screens.
 * maxWidth is set via inline style referencing the CSS variable so tests can
 * assert `el.style.maxWidth === 'var(--fm-content-max)'` without pixel magic.
 */
export const FmContentWidth: React.FC<FmContentWidthProps> = ({
  children,
  width = 'content',
  padY = 'none',
  className,
  'data-testid': dataTestId,
}) => (
  // A width constraint is transparent — it only bounds measure. Surfaces are
  // explicit (FmCard/FmControlSurface); painting an AntD theme background here
  // leaked a white box onto dark bands.
  <div
    className={cx(
      'fm-content-width',
      padY !== 'none' && `fm-content-width--pad-y-${padY}`,
      className,
    )}
    style={{ maxWidth: widthTokenFor(width) }}
    data-testid={dataTestId}
  >
    {children}
  </div>
);

// FmShellSection

export interface FmShellSectionProps {
  /** Optional header band (page title, breadcrumb, actions bar). */
  header?: React.ReactNode;
  /** Optional header actions. The shell owns their alignment and responsive wrap. */
  actions?: React.ReactNode;
  /** Main body content. */
  children: React.ReactNode;
  /** Optional aside panel (filters, contextual info). Solid — never glass. */
  aside?: React.ReactNode;
  /**
   * Device tier (FmResponsive contract). 'mobile' adds
   * fm-shell-section--mobile so CSS can collapse to minimum chrome. No
   * structural change in jsdom — the class is purely a styling hook.
   */
  tier?: FmDeviceTier;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  /**
   * @internal DS/migration class hook; forbidden for route-owned layout, colour,
   * spacing, and shell chrome. Add a named variant instead.
   */
  'data-testid'?: string;
}

/**
 * Page-body anatomy region: header band + body + optional aside.
 * Renders inside the E1 top-panel shell; NOT the shell chrome itself.
 * SOLID data surface: never translucent, never glass (CHROME-IS-GLASS principle).
 * Background + text color resolve through DS tokens; AntD does not own theming.
 */
export const FmShellSection: React.FC<FmShellSectionProps> = ({
  header,
  actions,
  children,
  aside,
  tier,
  className,
  'data-testid': dataTestId,
}) => {
  return (
    <section
      className={cx(
        'fm-shell-section',
        aside != null && 'fm-shell-section--has-aside',
        tier === 'mobile' && 'fm-shell-section--mobile',
        className,
      )}
      style={{ background: 'var(--fm-d-surface)', color: 'var(--fm-d-fg)' }}
      data-testid={dataTestId}
    >
      {header != null || actions != null ? (
        <div
          className="fm-shell-section__header"
          data-testid={dataTestId ? `${dataTestId}-header` : undefined}
          style={{ background: 'var(--fm-d-surface-2)' }}
        >
          <div className="fm-shell-section__header-main">{header}</div>
          {actions != null ? (
            <div className="fm-shell-section__header-actions">{actions}</div>
          ) : null}
        </div>
      ) : null}
      <div
        className={cx(
          'fm-shell-section__inner',
          aside != null && 'fm-shell-section__inner--with-aside',
        )}
      >
        <div
          className="fm-shell-section__body"
          data-testid={dataTestId ? `${dataTestId}-body` : undefined}
        >
          {children}
        </div>
        {aside != null ? (
          <div
            className="fm-shell-section__aside"
            data-testid={dataTestId ? `${dataTestId}-aside` : undefined}
          >
            {aside}
          </div>
        ) : null}
      </div>
    </section>
  );
};
